/**
 * Cancellation and refund maths. Pure functions — no database, no clock.
 *
 * The academy's rule, in full:
 *
 *   A member may cancel only within 14 days of their subscription STARTING.
 *   After that the term is theirs and there is no refund.
 *
 *   What they get back is the part of the bundle they did not use. A term is
 *   sold as a fixed number of sessions — 12 per month, so a 3-month bundle is
 *   36 — and one session is worth the bundle total divided by that count. The
 *   front desk enters how many sessions the member actually attended, and the
 *   rest comes back to them.
 *
 * The session price is derived from the TERM TOTAL, never from a monthly rate.
 * The schedule has bulk discounts baked in, so a 3-month session is genuinely
 * cheaper than a 1-month one, and that is the price the member paid.
 *
 * Everything here is integer piastres. The remainder from dividing a bundle
 * into sessions stays with the academy rather than being rounded up per
 * session, because rounding each session up and then multiplying could refund
 * more than the member ever paid.
 */
import type { ISODate, Subscription } from './types';
import { daysRemaining, formatDisplayDate, shiftDays } from './dates';

/**
 * Sessions in one month of any subscription.
 *
 * A constant rather than a setting: it is the number the price list was built
 * around, and changing it would silently re-value every past bundle in any
 * refund calculated afterwards.
 */
export const SESSIONS_PER_MONTH = 12;

/** Days from the start date during which a member may still cancel. */
export const CANCELLATION_WINDOW_DAYS = 14;

/** Total sessions in a term. A 3-month bundle is 36. */
export function sessionsInTerm(months: number): number {
  return Math.max(0, Math.trunc(months)) * SESSIONS_PER_MONTH;
}

/**
 * What one session of this bundle is worth, in piastres.
 *
 * Rounded DOWN, so `sessionPrice × sessions` can never exceed the bundle total
 * and a full-attendance cancellation can never produce a negative refund.
 */
export function sessionPricePiastres(totalDuePiastres: number, months: number): number {
  const sessions = sessionsInTerm(months);
  if (sessions <= 0) return 0;
  return Math.floor(Math.max(0, totalDuePiastres) / sessions);
}

/** The last day on which this subscription can still be cancelled. */
export function cancellationDeadline(startDate: ISODate): ISODate {
  return shiftDays(startDate, CANCELLATION_WINDOW_DAYS);
}

/**
 * Days left to cancel, counting today. Negative once the window has closed.
 *
 * Measured from the START DATE, not the sale: a term bought three weeks in
 * advance would otherwise lose its window before the member ever trained.
 */
export function daysLeftToCancel(startDate: ISODate, today: ISODate): number {
  return daysRemaining(cancellationDeadline(startDate), today);
}

/** Whether the 14-day window is still open. */
export function isWithinCancellationWindow(startDate: ISODate, today: ISODate): boolean {
  return daysLeftToCancel(startDate, today) >= 0;
}

export interface CancellationQuote {
  /** Sessions the whole term was sold as. */
  totalSessions: number;
  sessionPrice: number;
  /** Sessions the member used, clamped to the term. */
  sessionsAttended: number;
  /** sessionPrice × sessionsAttended — what they consumed. */
  consumed: number;
  /**
   * What the member is handed back: their money, less what they trained away.
   *
   * In the normal case — the member paid in full — this is exactly the bundle
   * total minus the sessions used, which is the academy's rule.
   *
   * It is expressed against what was PAID rather than against the bundle so a
   * part-paid subscription cannot refund money that was never collected. Note
   * that is not the same as capping the unused value at the amount paid: a
   * member who paid a quarter and then trained through half the bundle has
   * nothing left to give back, even though most of the bundle is unused.
   */
  refund: number;
  /**
   * Still owed after cancelling, when a part-paid member consumed more than
   * they handed over. Zero in the normal case.
   */
  stillOwed: number;
}

/**
 * Price a cancellation.
 *
 * `amountPaid` is what has actually been collected against the subscription,
 * so this is safe to call before knowing whether the member paid in full.
 */
export function quoteCancellation(
  subscription: Pick<Subscription, 'total_due_piastres' | 'months'>,
  sessionsAttended: number,
  amountPaidPiastres: number
): CancellationQuote {
  const totalSessions = sessionsInTerm(subscription.months);
  const sessionPrice = sessionPricePiastres(
    subscription.total_due_piastres,
    subscription.months
  );

  const attended = Math.min(
    Math.max(0, Math.trunc(sessionsAttended)),
    totalSessions
  );
  const consumed = sessionPrice * attended;

  const paid = Math.max(0, amountPaidPiastres);

  return {
    totalSessions,
    sessionPrice,
    sessionsAttended: attended,
    consumed,
    refund: Math.max(0, paid - consumed),
    stillOwed: Math.max(0, consumed - paid),
  };
}

/**
 * Field-level validation for the cancel dialog. Returns an empty object when
 * the cancellation may go ahead.
 */
export function validateCancellation(
  subscription: Pick<Subscription, 'months' | 'start_date' | 'cancelled'>,
  sessionsAttended: number,
  today: ISODate
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (subscription.cancelled) {
    errors.cancelled = 'This subscription is already cancelled';
    return errors;
  }

  if (!isWithinCancellationWindow(subscription.start_date, today)) {
    errors.window = `The 14-day cancellation window closed on ${formatDisplayDate(
      cancellationDeadline(subscription.start_date)
    )}`;
  }

  const total = sessionsInTerm(subscription.months);
  if (!Number.isInteger(sessionsAttended) || sessionsAttended < 0) {
    errors.sessionsAttended = 'Enter how many sessions they attended';
  } else if (sessionsAttended > total) {
    errors.sessionsAttended = `This bundle is only ${total} sessions`;
  }

  return errors;
}

/** Plain-language countdown for the cancel button's tooltip. */
export function describeCancellationWindow(startDate: ISODate, today: ISODate): string {
  const left = daysLeftToCancel(startDate, today);
  if (left < 0) {
    return `Cancellation closed on ${formatDisplayDate(
      cancellationDeadline(startDate)
    )} — 14 days after the start date`;
  }
  if (left === 0) return 'Last day to cancel';
  return left === 1 ? '1 day left to cancel' : `${left} days left to cancel`;
}
